import React, { useEffect, useState } from 'react';
import {
  Card, CardContent, Typography, Box, Chip, CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GavelIcon from '@mui/icons-material/Gavel';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import { getMyAgreementApi } from '../../api/auth';
import { getMyNDAApi } from '../../api/auth';

export default function Agreement() {
  const [agreement, setAgreement] = useState(null);
  const [nda, setNda] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getMyAgreementApi().catch(() => ({ data: { status: 'Pending' } })),
      getMyNDAApi().catch(() => ({ data: { status: 'Pending' } })),
    ]).then(([agrRes, ndaRes]) => {
      setAgreement(agrRes.data);
      setNda(ndaRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const isApproved = agreement?.status === 'Approved';
  const isNdaApproved = nda?.status === 'Approved';

  return (
    <Layout title="Agreement Certificate">
      <PageHeader
        title="Publisher Agreements"
        subtitle="View your agreement statuses"
        breadcrumbs={[{ label: 'Publisher' }, { label: 'Agreement' }]}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Onboarding Agreement */}
          <Card sx={{ border: 2, borderColor: isApproved ? 'success.main' : 'warning.main' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                {isApproved ? (
                  <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
                ) : (
                  <AccessTimeIcon sx={{ fontSize: 48, color: 'warning.main' }} />
                )}
                <Box>
                  <Typography variant="h5" color={isApproved ? 'success.main' : 'warning.main'}>
                    Onboarding Agreement
                  </Typography>
                  <Chip
                    label={isApproved ? 'Approved' : 'Pending'}
                    color={isApproved ? 'success' : 'warning'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>

              {isApproved ? (
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Your Publisher Onboarding Agreement has been reviewed and approved by the admin.
                  </Typography>
                  {agreement?.approval_date && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Approved on: {new Date(agreement.approval_date).toLocaleDateString()}
                    </Typography>
                  )}
                  {agreement?.remarks && (
                    <Typography variant="body2" color="text.secondary">
                      Remarks: {agreement.remarks}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Your Publisher Onboarding Agreement is currently under review by the admin.
                  You will be notified once it is approved.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* NDA Agreement */}
          <Card sx={{ border: 2, borderColor: isNdaApproved ? 'success.main' : 'warning.main' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                {isNdaApproved ? (
                  <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
                ) : (
                  <GavelIcon sx={{ fontSize: 48, color: 'warning.main' }} />
                )}
                <Box>
                  <Typography variant="h5" color={isNdaApproved ? 'success.main' : 'warning.main'}>
                    NDA Agreement
                  </Typography>
                  <Chip
                    label={isNdaApproved ? 'Approved' : 'Pending'}
                    color={isNdaApproved ? 'success' : 'warning'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>

              {isNdaApproved ? (
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Your NDA Agreement has been reviewed and approved by the admin.
                  </Typography>
                  {nda?.approval_date && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Approved on: {new Date(nda.approval_date).toLocaleDateString()}
                    </Typography>
                  )}
                  {nda?.remarks && (
                    <Typography variant="body2" color="text.secondary">
                      Remarks: {nda.remarks}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Your NDA Agreement is currently under review by the admin.
                  You will be notified once it is approved.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </Layout>
  );
}
